import { getAbsoluteUrl } from "$utils/urls.js";
import { feature } from "topojson-client";
import statesData from "$data/state_info.json";

/** @typedef {import("topojson-specification").Topology} Topology */
/** @typedef {import("topojson-specification").TopoJSON} TopoJSON */
/** @typedef {import("geojson").FeatureCollection} FeatureCollection */
/** @typedef {import("geojson").GeoJsonProperties} GeoJsonProperties */

/** @typedef {"states" | "pr" | "territories"} FeatureOptions */

export const statesInfo = statesData.sort((a, b) => a.state.localeCompare(b.state));

const stateNameLookup = statesInfo.reduce((acc, { state, fips }) => {
  return acc.set(fips, state);
}, new Map());

/**
 * @param {string} fips
 * @returns {string}
 */
export function getStateName(fips) {
  return stateNameLookup.get(fips);
}

/**
 * @param {FeatureOptions} option
 * @returns {Array<Object<any, any>>}
 */
export function getStatesInfo(option) {
  if (option === "states") {
    return statesInfo.filter(({ fips }) => parseInt(fips) < 60);
  }
  if (option === "pr") {
    return statesInfo.filter(({ fips }) => parseInt(fips) < 60 || parseInt(fips) === 72);
  }
  return statesInfo;
}

export class StateData {
  /** @type {FeatureCollection | undefined} */
  geoFeatures = undefined;

  /**
   * @param {FeatureOptions} option
   * @returns {Promise<Array<Object<any, any>>>}
   */
  async getFeatures(option = "states") {
    if (!this.geoFeatures) {
      const topoUrl = this.getTopoUrl();
      let resp = await fetch(topoUrl);
      if (resp.status !== 200) {
        throw new Error(`Failed to fetch ${topoUrl}`);
      }
      let result = await resp.json();
      const topoData = result;
      this.geoFeatures = this.parseTopo(topoData);
    }
    let filteredResult = this.filterFeatures(this.geoFeatures.features, option);
    return filteredResult;
  }

  /**
   * @param { Array<Object<any, any>> } allFeatures
   * @param { FeatureOptions } featureOption
   * @returns { Array<Object<any, any>> }
   */
  filterFeatures(allFeatures, featureOption) {
    if (featureOption === "states") {
      return allFeatures.filter((feature) => parseInt(feature.properties.fips) < 60);
    }
    if (featureOption === "pr") {
      return allFeatures.filter(
        (feature) =>
          parseInt(feature.properties.fips) < 60 || parseInt(feature.properties.fips) === 72
      );
    }
    return allFeatures;
  }

  /**
   * @param {Topology<Objects<GeoJsonProperties>>} topoData
   * @returns {FeatureCollection}
   */
  parseTopo(topoData) {
    return feature(topoData, "states");
  }

  getTopoUrl() {
    return getAbsoluteUrl("data/state_topo.json");
  }
}
